import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider, 
  Switch,
  FormControlLabel,
  Container,
  LinearProgress,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const RackForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    location: {
      zone: "general",
      aisle: "",
      row: "",
      section: "",
    },
    capacity: 100,
    temperatureRange: {
      min: "",
      max: "",
    },
    isActive: true,
  });

  useEffect(() => {
    if (isEdit) {
      fetchRack();
    }
  }, [id]);

  const fetchRack = async () => {
    try {
      setLoading(true);
      const response = await racksAPI.getById(id);
      const rack = response.data.data.rack;

      setFormData({
        name: rack.name || "",
        description: rack.description || "",
        location: {
          zone: rack.location?.zone || "general",
          aisle: rack.location?.aisle || "",
          row: rack.location?.row || "",
          section: rack.location?.section || "",
        },
        capacity: rack.capacity || 100,
        temperatureRange: {
          min: rack.temperatureRange?.min || "",
          max: rack.temperatureRange?.max || "",
        },
        isActive: rack.isActive !== undefined ? rack.isActive : true,
      });
    } catch (error) {
      console.error("Error fetching rack:", error);
      enqueueSnackbar("Error fetching rack details", { variant: "error" });
      navigate("/hospital/pharmacy/racks");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Rack name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Rack name must be at least 2 characters";
    }

    if (!formData.capacity || formData.capacity < 1) {
      newErrors.capacity = "Capacity must be at least 1";
    }

    if (formData.temperatureRange.min && formData.temperatureRange.max) {
      if (
        parseFloat(formData.temperatureRange.min) >
        parseFloat(formData.temperatureRange.max)
      ) {
        newErrors.temperatureRange =
          "Minimum temperature cannot be greater than maximum";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar("Please fix validation errors", { variant: "error" });
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        temperatureRange: {
          min: formData.temperatureRange.min
            ? parseFloat(formData.temperatureRange.min)
            : undefined,
          max: formData.temperatureRange.max
            ? parseFloat(formData.temperatureRange.max)
            : undefined,
        },
      };

      if (isEdit) {
        await racksAPI.update(id, submitData);
        enqueueSnackbar("Rack updated successfully", { variant: "success" });
      } else {
        await racksAPI.create(submitData);
        enqueueSnackbar("Rack created successfully", { variant: "success" });
      }

      navigate("/hospital/pharmacy/racks");
    } catch (error) {
      console.error("Save rack error:", error);
      const errorMessage =
        error.response?.data?.message ||
        `Error ${isEdit ? "updating" : "creating"} rack`;
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
        fontSize: '16px',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button 
              startIcon={<ArrowBack />} 
              onClick={() => navigate("/hospital/pharmacy/racks")}
              sx={{ fontSize: '1.1rem' }}
            >
              Back
            </Button>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {isEdit ? "Edit Rack" : "Create New Rack"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button 
              onClick={() => navigate("/hospital/pharmacy/racks")} 
              variant="outlined"
              sx={{ fontSize: '1.1rem' }}
            >
              Cancel
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
            >
              {saving ? "Saving..." : isEdit ? "Update Rack" : "Create Rack"}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Rack Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Rack Name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiFormHelperText-root': { fontSize: '1rem' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                      multiline
                      rows={3}
                      placeholder="Optional description of the rack..."
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Zone</InputLabel>
                      <Select
                        value={formData.location.zone}
                        label="Zone"
                        onChange={(e) =>
                          handleChange("location.zone", e.target.value)
                        }
                        sx={{ fontSize: '1.1rem' }}
                      >
                        <MenuItem value="general" sx={{ fontSize: '1.1rem' }}>General Storage</MenuItem>
                        <MenuItem value="cold_storage" sx={{ fontSize: '1.1rem' }}>Cold Storage</MenuItem>
                        <MenuItem value="controlled" sx={{ fontSize: '1.1rem' }}>Controlled Room</MenuItem>
                        <MenuItem value="hazardous" sx={{ fontSize: '1.1rem' }}>Hazardous Materials</MenuItem>
                        <MenuItem value="quarantine" sx={{ fontSize: '1.1rem' }}>Quarantine</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Aisle"
                      value={formData.location.aisle}
                      onChange={(e) =>
                        handleChange("location.aisle", e.target.value)
                      }
                      placeholder="e.g., A, B, C"
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Row"
                      value={formData.location.row}
                      onChange={(e) =>
                        handleChange("location.row", e.target.value)
                      }
                      placeholder="e.g., 1, 2, 3"
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Section"
                      value={formData.location.section}
                      onChange={(e) =>
                        handleChange("location.section", e.target.value)
                      }
                      placeholder="e.g., Left, Right"
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Capacity"
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => handleChange("capacity", e.target.value)}
                      error={!!errors.capacity}
                      helperText={
                        errors.capacity ||
                        "Maximum number of items this rack can hold"
                      }
                      required
                      inputProps={{ min: 1 }}
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiFormHelperText-root': { fontSize: '1rem' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ fontSize: '1.2rem' }}
                    >
                      Temperature Range (Optional)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Minimum Temperature (°C)"
                          type="number"
                          value={formData.temperatureRange.min}
                          onChange={(e) =>
                            handleChange("temperatureRange.min", e.target.value)
                          }
                          placeholder="e.g., 2"
                          sx={{
                            '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                            '& .MuiInputBase-input': { fontSize: '1.1rem' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Maximum Temperature (°C)"
                          type="number"
                          value={formData.temperatureRange.max}
                          onChange={(e) =>
                            handleChange("temperatureRange.max", e.target.value)
                          }
                          placeholder="e.g., 8"
                          sx={{
                            '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                            '& .MuiInputBase-input': { fontSize: '1.1rem' },
                          }}
                        />
                      </Grid>
                    </Grid>
                    {errors.temperatureRange && (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '1.1rem' }}>
                        {errors.temperatureRange}
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) =>
                            handleChange("isActive", e.target.checked)
                          }
                          color="primary"
                          size="large"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                          Active Rack
                        </Typography>
                      }
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1, fontSize: '1rem' }}
                    >
                      Inactive racks won't be available for new stock assignments
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ position: "sticky", top: 100 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Quick Tips
                </Typography>

                <Alert severity="info" sx={{ mb: 2, fontSize: '1.1rem' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Zone Guidelines:
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                    • General: Regular medicines
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                    • Cold Storage: 2-8°C medicines
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                    • Controlled: Room temperature sensitive
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                    • Hazardous: Dangerous substances
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                    • Quarantine: Returned/defective items
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  <strong>Capacity Planning:</strong> Set capacity based on
                  physical space and item sizes. You can adjust this later if
                  needed.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};